import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamsApi, TeamMember } from '../api/teams';
import { useAuth } from '../contexts/AuthContext';

export default function TeamDetail() {
  const { id } = useParams<{ id: string }>();
  const teamId = parseInt(id!);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);

  const { data: team, isLoading } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamsApi.get(teamId).then((r) => r.data),
  });

  const { data: members = [] } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => teamsApi.getMembers(teamId).then((r) => r.data),
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) => teamsApi.inviteMember(teamId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      setInviteEmail('');
      setShowInviteForm(false);
      setInviteError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setInviteError(e.response?.data?.detail ?? 'Fehler beim Einladen');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => teamsApi.removeMember(teamId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team-members', teamId] }),
  });

  const saveRankingMutation = useMutation({
    mutationFn: (rankings: { user_id: string; ranking_position: number }[]) =>
      teamsApi.updateRanking(teamId, rankings),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team-members', teamId] }),
  });

  const [rankingEdit, setRankingEdit] = useState<Record<string, number>>({});
  const [editingRanking, setEditingRanking] = useState(false);

  const isCaptain = user && team && team.captain_id === user.id;

  const activeMembers = members.filter((m) => m.is_active && m.invite_status === 'accepted');
  const pendingMembers = members.filter((m) => m.invite_status === 'pending');

  function roleLabel(role: TeamMember['role']) {
    if (role === 'captain') return 'Kapitän';
    if (role === 'co_captain') return 'Co-Kapitän';
    return 'Spieler';
  }

  function startRankingEdit() {
    const initial: Record<string, number> = {};
    activeMembers.forEach((m) => {
      initial[m.user_id] = m.ranking_position ?? 0;
    });
    setRankingEdit(initial);
    setEditingRanking(true);
  }

  function saveRanking() {
    const rankings = Object.entries(rankingEdit).map(([uid, pos]) => ({
      user_id: uid,
      ranking_position: pos,
    }));
    saveRankingMutation.mutate(rankings, { onSuccess: () => setEditingRanking(false) });
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
          <div className="h-48 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (!team) {
    return <div className="text-center py-16 text-gray-500">Team nicht gefunden</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{team.name}</h1>
            {team.home_location && (
              <p className="text-sm text-gray-500 mt-1">📍 {team.home_location}</p>
            )}
          </div>
          <span className="badge border border-orange-300 bg-orange-50 text-orange-700">Mannschaft</span>
        </div>
      </div>

      {/* Pending invitations (captain view) */}
      {isCaptain && pendingMembers.length > 0 && (
        <div className="card border border-yellow-200 bg-yellow-50">
          <h2 className="text-sm font-semibold text-yellow-800 mb-3">
            Ausstehende Einladungen ({pendingMembers.length})
          </h2>
          <div className="space-y-2">
            {pendingMembers.map((member) => (
              <div key={member.user_id} className="flex items-center justify-between">
                <span className="text-sm text-yellow-900">
                  {member.user
                    ? <Link to={`/users/${member.user_id}`} className="hover:text-primary-600">{member.user.first_name} {member.user.last_name}</Link>
                    : member.user_id}
                </span>
                <button
                  onClick={() => removeMutation.mutate(member.user_id)}
                  disabled={removeMutation.isPending}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Einladung zurückziehen
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active members */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            Kader ({activeMembers.length} Spieler)
          </h2>
          <div className="flex flex-wrap gap-2">
            {isCaptain && !editingRanking && (
              <>
                <button onClick={startRankingEdit} className="btn btn-secondary btn-sm">
                  Ranking bearbeiten
                </button>
                <button onClick={() => setShowInviteForm(!showInviteForm)} className="btn btn-primary btn-sm">
                  + Einladen
                </button>
              </>
            )}
            {isCaptain && editingRanking && (
              <>
                <button onClick={saveRanking} disabled={saveRankingMutation.isPending} className="btn btn-primary btn-sm">
                  Speichern
                </button>
                <button onClick={() => setEditingRanking(false)} className="btn btn-secondary btn-sm">
                  Abbrechen
                </button>
              </>
            )}
          </div>
        </div>

        {showInviteForm && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            {inviteError && <div className="text-red-600 text-sm mb-2">{inviteError}</div>}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="E-Mail des Spielers"
                className="input flex-1 text-sm"
              />
              <button
                onClick={() => inviteMutation.mutate(inviteEmail)}
                disabled={inviteMutation.isPending || !inviteEmail}
                className="btn btn-primary btn-sm"
              >
                {inviteMutation.isPending ? '...' : 'Einladen'}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {activeMembers.map((member, idx) => (
            <div key={member.user_id} className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0 flex-wrap sm:flex-nowrap">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-5 text-center font-mono">
                  {editingRanking ? (
                    <input
                      type="number"
                      min="1"
                      value={rankingEdit[member.user_id] ?? idx + 1}
                      onChange={(e) =>
                        setRankingEdit((prev) => ({
                          ...prev,
                          [member.user_id]: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="w-10 border border-gray-300 rounded px-1 py-0 text-xs"
                    />
                  ) : (
                    member.ranking_position ?? '–'
                  )}
                </span>
                <div>
                  {member.user
                    ? <Link to={`/users/${member.user_id}`} className="font-medium text-gray-900 hover:text-primary-600">{member.user.first_name} {member.user.last_name}</Link>
                    : <span className="font-medium text-gray-900">{member.user_id}</span>}
                  {member.user?.level && (
                    <span className="ml-2 text-xs text-gray-400">{member.user.level}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`badge text-xs ${
                    member.role === 'captain'
                      ? 'bg-orange-100 text-orange-700'
                      : member.role === 'co_captain'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {roleLabel(member.role)}
                </span>
                {isCaptain && member.role !== 'captain' && !editingRanking && (
                  <button
                    onClick={() => removeMutation.mutate(member.user_id)}
                    disabled={removeMutation.isPending}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Entfernen
                  </button>
                )}
              </div>
            </div>
          ))}
          {activeMembers.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">Noch keine aktiven Mitglieder</p>
          )}
        </div>
      </div>
    </div>
  );
}
